/**
 * Folder Item Component
 *
 * Renders a single folder item in the folder tree with:
 * - Expand/collapse toggle
 * - Navigation on double-click
 * - Inline rename
 * - Drag and drop support
 * - Context menu actions
 *
 * @module features/organization/components/FolderItem
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Folder,
  FolderOpen,
  FolderPlus,
  MoreHorizontal,
  Pencil,
  Trash2,
  GripVertical,
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuAction,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@loader/ui';
import type { FolderTreeNode } from '@loader/shared-types';

import { useUpdateFolderMutation } from '../hooks';

import { useToastStore } from '@/store/useToasts';
import { useUIStore } from '@/store/useUI';

// ============================================================================
// TYPES
// ============================================================================

export interface FolderItemProps {
  folder: FolderTreeNode;
  level?: number;
  /** Whether user can edit (editor/owner) - viewers cannot modify folders */
  canEdit?: boolean;
  onDelete?: (id: string, name: string) => void;
  onCreateSubfolder?: (parentId: string, parentName: string) => void;
  onDragStart?: (e: React.DragEvent, folder: FolderTreeNode) => void;
  onDragOver?: (e: React.DragEvent, folder: FolderTreeNode) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, targetFolder: FolderTreeNode) => void;
  dragOverFolderId?: string | null;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Folder item component for the sidebar tree
 */
export function FolderItem({
  folder,
  level = 0,
  canEdit = true,
  onDelete,
  onCreateSubfolder,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  dragOverFolderId,
}: FolderItemProps) {
  const navigate = useNavigate();
  const router = useRouterState();
  const currentPath = router.location.pathname;
  const inputRef = useRef<HTMLInputElement>(null);
  const { success: showSuccess, error: showError } = useToastStore();
  const updateFolderMutation = useUpdateFolderMutation();

  // Persistent expanded state from store
  const { expandedFolders, toggleFolderExpanded, expandFolders } = useUIStore();

  // Check if this folder is active (current route)
  const folderId = folder.id;
  const isActive = currentPath === `/workspace/folder/${folderId}`;
  const hasChildren = folder.children && folder.children.length > 0;

  // Check if expanded from persistent store
  const isExpanded = expandedFolders.has(folderId);

  // Auto-expand when first navigating to this folder (not continuously)
  const wasActiveRef = useRef(false);
  useEffect(() => {
    if (isActive && !wasActiveRef.current && hasChildren && !isExpanded) {
      toggleFolderExpanded(folderId);
    }
    wasActiveRef.current = isActive;
  }, [isActive, hasChildren, folderId, isExpanded, toggleFolderExpanded]);

  // Auto-expand ancestors when a descendant is active
  useEffect(() => {
    const getAncestorPath = (
      nodes: FolderTreeNode[],
      targetId: string,
      path: string[] = []
    ): string[] | null => {
      for (const node of nodes) {
        if (node.id === targetId) return path;
        if (node.children?.length) {
          const result = getAncestorPath(node.children, targetId, [...path, node.id]);
          if (result) return result;
        }
      }
      return null;
    };

    // Check if current path is viewing a folder
    const match = currentPath.match(/\/workspace\/folder\/([^/]+)/);
    if (match) {
      const viewingFolderId = match[1];
      if (folder.children?.length) {
        const ancestorPath = getAncestorPath([folder], viewingFolderId);
        if (ancestorPath && ancestorPath.length > 0) {
          expandFolders(ancestorPath);
        }
      }
    }
  }, [currentPath, folder, expandFolders]);

  // Track hover on this row only
  const [isRowHovered, setIsRowHovered] = useState(false);

  // Inline rename state
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(folder.name);

  // Drag state
  const isDragOver = dragOverFolderId === folderId;

  // Focus input when entering rename mode
  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const handleStartRename = () => {
    if (!canEdit) return;
    setRenameValue(folder.name);
    setIsRenaming(true);
  };

  const handleCancelRename = () => {
    setIsRenaming(false);
    setRenameValue(folder.name);
  };

  const handleSaveRename = () => {
    const trimmedName = renameValue.trim();
    if (!trimmedName) {
      showError('Folder name cannot be empty');
      return;
    }
    if (trimmedName === folder.name) {
      setIsRenaming(false);
      return;
    }

    updateFolderMutation.mutate(
      { id: folder.id, data: { name: trimmedName } },
      {
        onSuccess: () => {
          showSuccess('Folder renamed');
          setIsRenaming(false);
        },
        onError: (err) => {
          showError('Failed to rename folder', err instanceof Error ? err.message : 'Unknown error');
        },
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveRename();
    } else if (e.key === 'Escape') {
      handleCancelRename();
    }
  };

  // Single click - expand/collapse
  const handleSingleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (hasChildren) {
        toggleFolderExpanded(folderId);
      }
    },
    [hasChildren, folderId, toggleFolderExpanded]
  );

  // Double click - navigate
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      void navigate({ to: '/workspace/folder/$id', params: { id: folderId } });
    },
    [navigate, folderId]
  );

  return (
    <Collapsible asChild open={isExpanded}>
      <SidebarMenuItem>
        {/* Row wrapper - handles hover for both button and action */}
        <div
          className={`relative flex items-center ${isDragOver ? 'bg-accent rounded-md' : ''}`}
          onMouseEnter={() => setIsRowHovered(true)}
          onMouseLeave={() => setIsRowHovered(false)}
          draggable={!isRenaming && canEdit}
          onDragStart={(e) => canEdit && onDragStart?.(e, folder)}
          onDragOver={(e) => canEdit && onDragOver?.(e, folder)}
          onDragLeave={(e) => onDragLeave?.(e)}
          onDrop={(e) => canEdit && onDrop?.(e, folder)}
        >
          {/* Drag handle - visible on hover, hidden during rename, when collapsed, or for viewers */}
          <div
            className={`px-0.5 group-data-[collapsible=icon]:hidden ${
              canEdit ? 'cursor-grab' : 'cursor-not-allowed'
            } ${isRowHovered && !isRenaming && canEdit ? 'opacity-50' : 'opacity-0'}`}
          >
            <GripVertical className="size-3" />
          </div>
          <SidebarMenuButton
            tooltip={folder.name}
            isActive={isActive}
            className={`flex-1 data-[active=true]:bg-transparent data-[active=true]:font-normal ${isRowHovered ? 'bg-sidebar-accent! text-sidebar-accent-foreground!' : ''}`}
          >
            <span
              onClick={isRenaming ? undefined : handleSingleClick}
              className="cursor-pointer"
            >
              {isExpanded || isActive ? (
                <FolderOpen className="size-4 fill-amber-500 text-amber-600" />
              ) : (
                <Folder className="size-4 fill-amber-500 text-amber-600" />
              )}
            </span>
            {isRenaming ? (
              <input
                ref={inputRef}
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleSaveRename}
                onClick={(e) => e.stopPropagation()}
                className="flex-1 bg-transparent outline-none"
              />
            ) : (
              <span
                className="flex-1 cursor-pointer truncate"
                onClick={handleDoubleClick}
              >
                {folder.name}
              </span>
            )}
          </SidebarMenuButton>

          {/* 3 dots - shows on row hover, hidden during rename */}
          {!isRenaming && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuAction
                  className={`hover:bg-transparent ${isRowHovered ? 'opacity-100' : 'opacity-0'} data-[state=open]:opacity-100`}
                >
                  <MoreHorizontal className="size-4" />
                  <span className="sr-only">More options</span>
                </SidebarMenuAction>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" align="start">
                <span className={!canEdit ? 'cursor-not-allowed' : ''}>
                  <DropdownMenuItem
                    disabled={!canEdit}
                    onClick={canEdit ? () => onCreateSubfolder?.(folder.id, folder.name) : undefined}
                  >
                    <FolderPlus className="mr-2 size-4 fill-amber-500 text-amber-600" />
                    New Subfolder
                  </DropdownMenuItem>
                </span>
                <span className={!canEdit ? 'cursor-not-allowed' : ''}>
                  <DropdownMenuItem
                    disabled={!canEdit}
                    onClick={canEdit ? handleStartRename : undefined}
                  >
                    <Pencil className="mr-2 size-4" />
                    Rename
                  </DropdownMenuItem>
                </span>
                <span className={!canEdit ? 'cursor-not-allowed' : ''}>
                  <DropdownMenuItem
                    disabled={!canEdit}
                    onClick={canEdit ? () => onDelete?.(folder.id, folder.name) : undefined}
                    className="text-destructive"
                  >
                    <Trash2 className="mr-2 size-4" />
                    Move to Trash
                  </DropdownMenuItem>
                </span>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Children folders */}
        {hasChildren && (
          <CollapsibleContent>
            <SidebarMenuSub className="mx-0 ml-4 border-l-0 px-0 pr-0">
              {folder.children.map((child) => (
                <FolderItem
                  key={child.id}
                  folder={child}
                  level={level + 1}
                  canEdit={canEdit}
                  onDelete={onDelete}
                  onCreateSubfolder={onCreateSubfolder}
                  onDragStart={onDragStart}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  dragOverFolderId={dragOverFolderId}
                />
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        )}
      </SidebarMenuItem>
    </Collapsible>
  );
}
